// One relay gift waiting in this app (docs/features/relay-passes.md › Gifts › From a phone): its token in memory,
// the Macs whose links came up, the person's choice, the Mac's answer — and what the card says at each step. No
// DOM, no storage, no network of its own (`describe` and the links are handed in), so it runs under Node
// (services/app/test/relaygift.test.mjs).
//
//   waiting  no Mac connected yet (or this browser paired with none)       Not Now
//   ask      "Send 1 GB of relay data to ‹Mac›?" (a picker with several)   Not Now · Send
//   sending  sent over that Mac's link as `relayGift` {t, token}; the Mac claims it the way its cody:// link does
//   done     "Received 1 GB on ‹Mac›"                                      Done
//   failed   the Mac's refusal (or no answer)                              Not Now · Try Again — or Close when spent
//   spent    the site says it was received already                         Close
//   gone     dismissed: the token is dropped
//
// The wire, both ways: → {t: 'relayGift', token} · ← {t: 'relayGift', ok: true, gb} | {t: 'relayGift', error, code}
export const WAIT = 45000

export class GiftOffer {
  ///   describe  async (token) → {passes, spent} from the site, or throws (the amount is then "this relay data")
  ///   paired    () → whether this browser is paired with any Mac
  ///   log       (line) → the client's log; never handed the token
  constructor(token, { describe = null, paired = () => true, log = () => {}, wait = WAIT, onChange = () => {} } = {}) {
    this.token = token
    this.state = 'waiting'
    this.gb = null
    this.macs = new Map()
    this.chosen = null
    this.problem = null       // {text, retry}
    this.received = null      // {gb, mac}
    this.paired = paired
    this.log = log
    this.wait = wait
    this.onChange = onChange
    this.timer = null
    if (describe) describe(token).then((d) => {
      if (this.state === 'gone') return
      if (Number.isInteger(d?.passes) && d.passes > 0) this.gb = d.passes
      if (d?.spent && (this.state === 'waiting' || this.state === 'ask')) this.state = 'spent'
      this.changed()
    }, (e) => {
      if (this.state === 'gone' || e?.status !== 404) return   // not reached: the card says "this relay data"
      this.state = 'failed'; this.problem = { text: 'That relay gift doesn’t exist.', retry: false }; this.changed()
    })
  }

  changed() { this.onChange(this) }

  /// The Macs to offer: connected now, with their wire link up.
  online() { return [...this.macs.values()].filter((m) => m.link === 'online' && m.windows && !m.paused) }

  /// A Mac's link came up (app.js, once per link): the card asks once there is one.
  add(mac) {
    if (this.state === 'gone') return
    this.macs.set(mac.key, mac)
    this.tick()
  }

  /// Links come and go: the card follows (app.js's link-up call, and a slow tick while it shows).
  tick() {
    const online = this.online()
    if (this.state === 'waiting' && online.length) this.state = 'ask'
    if (this.state === 'ask' && !online.length) this.state = 'waiting'
    if (!online.some((m) => m.key === this.chosen)) this.chosen = online[0]?.key ?? null
    this.changed()
  }

  choose(key) { if (this.macs.has(key)) { this.chosen = key; this.changed() } }

  mac() { return this.macs.get(this.chosen) || null }

  send() {
    const mac = this.mac(), link = mac?.windows
    if (!link || mac.link !== 'online') { this.problem = { text: (mac?.name || 'That Mac') + ' isn’t connected right now.', retry: true }; this.state = 'failed'; this.changed(); return }
    this.state = 'sending'; this.problem = null
    const settle = (message) => {
      if (link.onRelayGift === settle) link.onRelayGift = null
      clearTimeout(this.timer)
      if (this.state !== 'sending') return
      this.answered(mac, message)
    }
    link.onRelayGift = settle
    this.timer = setTimeout(() => settle({ error: mac.name + ' didn’t answer. Its Cody may be from before gifts could be sent this way — update it, or open the gift link on the Mac.', code: 'no-answer' }), this.wait)
    link.send('relayGift', { t: 'relayGift', token: this.token })
    this.log('relay gift: sent to a Mac — waiting for its answer')
    this.changed()
  }

  answered(mac, message) {
    if (message?.ok && Number.isInteger(message.gb)) {
      this.state = 'done'; this.received = { gb: message.gb, mac: mac.name }
      this.token = null   // spent: nothing of it is kept
      this.log('relay gift: received on the Mac — ' + message.gb + ' GB')
    } else {
      const spent = message?.code === 'already-received'
      this.state = 'failed'
      this.problem = { text: String(message?.error || 'The Mac couldn’t take that gift.'), retry: !spent && message?.code !== 'not-a-gift' }
      this.log('relay gift: the Mac refused it — ' + (message?.code || 'error'))
    }
    this.changed()
  }

  /// Not Now, Close, Done: the token is dropped and the card goes.
  dismiss() {
    clearTimeout(this.timer)
    for (const m of this.macs.values()) if (m.windows?.onRelayGift) m.windows.onRelayGift = null
    this.state = 'gone'; this.token = null; this.macs.clear()
    this.changed()
  }

  /// What the card shows: {title, note, choices, chosen, buttons: [{label, act, primary}]}, or null when nothing.
  view() {
    const amount = this.gb ? this.gb + ' GB of relay data' : 'this relay data'
    const online = this.online(), name = this.mac()?.name || 'your Mac'
    const notNow = { label: 'Not Now', act: 'dismiss' }
    switch (this.state) {
      case 'gone': return null
      case 'waiting': return this.paired()
        ? { title: 'Send ' + amount + ' to your Mac?', note: 'Waiting for your Mac to connect…', buttons: [notNow] }
        : { title: 'Relay data for your Mac', note: 'This browser isn’t paired with a Mac yet. Pair it (on the Mac: Devices › Pair a device), then open the gift link again.', buttons: [notNow] }
      case 'ask': return {
        title: 'Send ' + amount + ' to ' + (online.length > 1 ? '' : name + '?'),
        choices: online.length > 1 ? online.map((m) => ({ key: m.key, name: m.name })) : null, chosen: this.chosen,
        note: 'It is received on that Mac, for connecting through Cody’s relay. It works once.',
        buttons: [notNow, { label: 'Send', act: 'send', primary: true }],
      }
      case 'sending': return { title: 'Sending ' + amount + ' to ' + name + '…', buttons: [] }
      case 'done': return { title: 'Received ' + this.received.gb + ' GB on ' + this.received.mac, buttons: [{ label: 'Done', act: 'dismiss', primary: true }] }
      case 'spent': return { title: 'This relay data was already received.', buttons: [{ label: 'Close', act: 'dismiss' }] }
      case 'failed': return {
        title: this.problem?.text || 'That didn’t work.',
        buttons: this.problem?.retry && this.token ? [notNow, { label: 'Try Again', act: 'retry', primary: true }] : [{ label: 'Close', act: 'dismiss' }],
      }
    }
    return null
  }

  /// A button: 'send' · 'retry' (back to ask, or straight on when the same Mac is still there) · 'dismiss'.
  act(what) {
    if (what === 'dismiss') return this.dismiss()
    if (what === 'retry') { this.state = this.online().length ? 'ask' : 'waiting'; this.problem = null; this.tick(); return }
    if (what === 'send' && this.state === 'ask') this.send()
  }
}
